import { CopyOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Modal, Tooltip, Typography, message } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { BEHAVIOUR_LABELS, BEHAVIOUR_OPTIONS } from '../../../common/constants';
import CommonTable from '../../../components/CommonTable';
import { INCIDENT_LOG_SUMMARY } from './graphql/Queries';

const { Text, Title } = Typography;

// Every month starts at a flat 10.0 — this isn't derived from anything
// server-side (attendanceScore's `breakdown[key].deduction` turned out to
// just echo BEHAVIOUR_OPTIONS' static catalog weights regardless of what
// actually happened that month — it always sums to 5.5 no matter the
// employee/month — so it can't be used to compute Base or Total Deductions).
const BASE_SCORE = 10;

// snake_case behaviourKey -> per-occurrence deduction weight (same weights
// shown in the Log Incident form's dropdown labels).
const DEDUCTION_BY_BEHAVIOUR_KEY = BEHAVIOUR_OPTIONS.reduce(
  (map, option) => ({ ...map, [option.value]: option.deduction }),
  {},
);

const getUserName = (user) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '-';

const COLUMNS = [
  {
    title: 'Date',
    dataIndex: 'loggedAt',
    key: 'loggedAt',
    width: 110,
    render: (value) => (value ? dayjs(value).format('DD MMM YYYY') : '-'),
  },
  {
    title: 'Incident Type',
    dataIndex: 'behaviourKey',
    key: 'behaviourKey',
    width: 140,
    render: (value) => BEHAVIOUR_LABELS[value] || value,
  },
  {
    title: 'Remarks',
    dataIndex: 'reason',
    key: 'reason',
    width: 200,
    ellipsis: {
      showTitle: false,
    },
    render: (value, record) => {
      const remarks = value || record?.excuseReason || '-';
      return (
        <Tooltip placement="topLeft" title={remarks}>
          <span>{remarks}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Count',
    dataIndex: 'scoreImpact',
    key: 'scoreImpact',
    align: 'center',
    width: 80,
    render: (value) => value || '—',
  },
  {
    title: 'Logged By',
    dataIndex: 'loggedByUser',
    key: 'loggedByUser',
    width: 140,
    ellipsis: true,
    render: (value) => getUserName(value),
  },
  {
    title: 'Deduction',
    dataIndex: 'deduction',
    key: 'deduction',
    width: 100,
    align: 'center',
    render: (value) =>
      value ? (
        <Text type="danger" strong>
          {`-${Math.abs(value).toFixed(2)}`}
        </Text>
      ) : (
        '-'
      ),
  },
];

/**
 * IncidentBreakdownModal shows the incidents behind a single month's
 * attendance score for one employee, sourced from the attendanceIncidents
 * query. Every month starts at a flat Base Score of 10.0, so Total
 * Deductions is simply 10 − finalScore (finalScore is the server's actual
 * computed score for the month, already authoritative). Every row's
 * "Deduction" column shows that behaviour's static weight (from
 * BEHAVIOUR_OPTIONS), unconditionally — including excused/archived rows —
 * since it's informational ("what this behaviour costs"), not a per-row
 * slice that's expected to sum to Total Deductions.
 */
function IncidentBreakdownModal({
  open,
  loading,
  employeeId,
  employeeName,
  month,
  year,
  monthLabel,
  finalScore,
  incidents,
  onChange,
  paginationConfig,
  onCancel,
}) {
  const [incidentSummary, setIncidentSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [copyingSummary, setCopyingSummary] = useState(false);
  const [fetchIncidentLogSummary] = useLazyQuery(INCIDENT_LOG_SUMMARY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setSummaryLoading(true);
    fetchIncidentLogSummary({
      variables: { where: { employeeId, month, year } },
    })
      .then(({ data }) => {
        setIncidentSummary(data?.attendanceIncidentSummary?.summary || null);
      })
      .finally(() => setSummaryLoading(false));
  }, [open, employeeId, month, year, fetchIncidentLogSummary]);

  const handleCopySummary = async () => {
    if (!incidentSummary) {
      message.error('No summary available to copy');
      return;
    }
    setCopyingSummary(true);
    try {
      // eslint-disable-next-line no-undef
      await navigator.clipboard.writeText(incidentSummary);
      message.success('Incident summary copied to clipboard');
    } catch (err) {
      message.error(err?.message || 'Failed to copy incident summary');
    } finally {
      setCopyingSummary(false);
    }
  };

  const rows = (incidents || [])
    .map((incident) => ({
      ...incident,
      deduction: DEDUCTION_BY_BEHAVIOUR_KEY[incident.behaviourKey] || 0,
    }))
    .sort((a, b) => dayjs(b.loggedAt).valueOf() - dayjs(a.loggedAt).valueOf());

  const hasFinalScore = finalScore !== undefined && finalScore !== null;
  const totalDeductions = hasFinalScore
    ? Math.max(BASE_SCORE - finalScore, 0)
    : null;
  let totalDeductionsLabel = 'N/A';
  if (hasFinalScore) {
    totalDeductionsLabel = totalDeductions
      ? `-${totalDeductions.toFixed(2)}`
      : '0.00';
  }

  const titleParts = [employeeName, monthLabel].filter(Boolean);

  return (
    <Modal
      rootClassName="incident-breakdown-modal"
      title={
        titleParts.length
          ? `Incident Breakdown – ${titleParts.join(' · ')}`
          : 'Incident Breakdown'
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={820}
    >
      <div className="incident-breakdown-summary">
        <div className="incident-breakdown-stat">
          <Text type="secondary">Base Score</Text>
          <Title level={4}>{BASE_SCORE.toFixed(2)}</Title>
        </div>
        <div className="incident-breakdown-stat">
          <Text type="secondary">Total Deductions</Text>
          <Title level={4} type={totalDeductions ? 'danger' : undefined}>
            {totalDeductionsLabel}
          </Title>
        </div>
        <div className="incident-breakdown-stat">
          <Text type="secondary">Final Score</Text>
          <Title level={4}>
            {hasFinalScore ? finalScore.toFixed(2) : 'N/A'}
          </Title>
        </div>
        <Tooltip title="Copy incident summary">
          <Button
            className="incident-breakdown-copy-btn"
            icon={<CopyOutlined />}
            loading={copyingSummary || summaryLoading}
            disabled={!incidentSummary}
            onClick={handleCopySummary}
          >
            Copy
          </Button>
        </Tooltip>
      </div>
      <CommonTable
        size="small"
        bordered
        loadingData={loading}
        columns={COLUMNS}
        data={rows}
        rowKey={(obj) => obj?.id}
        onChange={onChange}
        paginationConfig={paginationConfig}
        scroll={{ x: 810 }}
        locale={{
          emptyText: (
            <Text type="secondary">
              No incidents recorded for this month. Full score retained.
            </Text>
          ),
        }}
      />
    </Modal>
  );
}

export default IncidentBreakdownModal;
